import React, { createContext, useContext, useState, useRef, ReactNode, useEffect } from 'react';
import { Stage } from 'amazon-ivs-web-broadcast';
import { logger } from '../utils/logger';

export interface BroadcastStream {
  id: string;
  name: string;
  streamingMode: 'browser' | 'whip';
}

export type BroadcastStatus = 'idle' | 'connecting' | 'live' | 'stopping' | 'error';

interface BroadcastContextType {
  currentBroadcast: BroadcastStream | null;
  status: BroadcastStatus;
  error: string | null;
  audioLevel: number;
  stageRef: React.MutableRefObject<Stage | null>;
  mediaStreamRef: React.MutableRefObject<MediaStream | null>;
  startBroadcast: (stream: BroadcastStream, stage: Stage, mediaStream: MediaStream) => void;
  stopBroadcast: () => void;
  setStatus: (status: BroadcastStatus) => void;
  setError: (error: string | null) => void;
}

const BroadcastContext = createContext<BroadcastContextType | undefined>(undefined);

export const useBroadcast = () => {
  const context = useContext(BroadcastContext);
  if (!context) {
    throw new Error('useBroadcast must be used within BroadcastProvider');
  }
  return context;
};

export const BroadcastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentBroadcast, setCurrentBroadcast] = useState<BroadcastStream | null>(null);
  const [status, setStatus] = useState<BroadcastStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [audioLevel, setAudioLevel] = useState<number>(0);

  const stageRef = useRef<Stage | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  // Audio level monitoring effect
  useEffect(() => {
    if (status === 'live' && mediaStreamRef.current) {
      logger.info('📊 Starting audio level monitoring in context...');

      try {
        // Create audio context and analyser
        const audioContext = new AudioContext();
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.8;

        const source = audioContext.createMediaStreamSource(mediaStreamRef.current);
        source.connect(analyser);

        audioContextRef.current = audioContext;
        analyserRef.current = analyser;

        const dataArray = new Uint8Array(analyser.frequencyBinCount);

        // Animation loop for audio monitoring
        const monitorAudio = () => {
          if (analyserRef.current && status === 'live') {
            analyserRef.current.getByteFrequencyData(dataArray);

            // Calculate RMS (root mean square) for audio level
            let sum = 0;
            for (let i = 0; i < dataArray.length; i++) {
              sum += dataArray[i] * dataArray[i];
            }
            const rms = Math.sqrt(sum / dataArray.length);
            const normalized = Math.min(100, (rms / 128) * 100);

            setAudioLevel(normalized);
            logger.info(`🎚️ Audio level: ${normalized.toFixed(2)}`);

            animationFrameRef.current = requestAnimationFrame(monitorAudio);
          }
        };

        animationFrameRef.current = requestAnimationFrame(monitorAudio);
        logger.info('✅ Audio monitoring loop started in context');

      } catch (err) {
        logger.error('❌ Error starting audio monitoring:', err);
      }
    }

    // Cleanup audio monitoring
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
      analyserRef.current = null;
      setAudioLevel(0);
      logger.info('🛑 Audio monitoring stopped in context');
    };
  }, [status]);

  const startBroadcast = (stream: BroadcastStream, stage: Stage, mediaStream: MediaStream) => {
    logger.info('🎙️ BroadcastContext: Starting broadcast', stream);
    setCurrentBroadcast(stream);
    stageRef.current = stage;
    mediaStreamRef.current = mediaStream;
    setStatus('live');
    setError(null);
  };

  const stopBroadcast = () => {
    logger.info('🛑 BroadcastContext: Stopping broadcast');
    console.trace('📍 Stack trace - WHO called stopBroadcast?');

    // Stop audio monitoring
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    // Leave the stage if it exists
    if (stageRef.current) {
      try {
        stageRef.current.leave();
      } catch (err) {
        logger.error('Error leaving stage:', err);
      }
      stageRef.current = null;
    }

    // Stop media stream
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }

    setCurrentBroadcast(null);
    setStatus('idle');
    setError(null);
    setAudioLevel(0);
  };

  const value: BroadcastContextType = {
    currentBroadcast,
    status,
    error,
    audioLevel,
    stageRef,
    mediaStreamRef,
    startBroadcast,
    stopBroadcast,
    setStatus,
    setError,
  };

  return (
    <BroadcastContext.Provider value={value}>
      {children}
    </BroadcastContext.Provider>
  );
};
